import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useUser } from "@clerk/clerk-react";
import Loader from '../components/Loader';
import { api } from '../services/api';
import { HARDWARE_ITEMS } from '../data/hardware';
import { DAILY_CHALLENGES } from '../data/dailyChallenges';
import { MISSIONS } from '../data/missions';

const GameContext = createContext();

export const useGame = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
  const { user, isLoaded, isSignedIn } = useUser();
  const [dataLoaded, setDataLoaded] = useState(false);



  // --- CORE STATE ---
  const [balance, setBalance] = useState(5000);
  const [cp, setCp] = useState(0);
  const [gems, setGems] = useState(1);
  const [totalSpins, setTotalSpins] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [questionsCorrect, setQuestionsCorrect] = useState(0);
  const [answerStreak, setAnswerStreak] = useState(0);
  const [lifelinesUsed, setLifelinesUsed] = useState(0);
  const [prestigeLevel, setPrestigeLevel] = useState(0);
  const [completedChallengeIds, setCompletedChallengeIds] = useState([]);

  // --- INVENTORY & HARDWARE ---
  const [inventory, setInventory] = useState([]);
  const [equipped, setEquipped] = useState({});

  // --- GAMEPLAY FEATURES ---
  const [flippedCards, setFlippedCards] = useState([]);
  const [lastFlipDate, setLastFlipDate] = useState(new Date().toDateString());
  const [defeatedBosses, setDefeatedBosses] = useState([]);

  // --- MISSION & DAILY STATE ---
  const [completedMissions, setCompletedMissions] = useState([]);
  const [claimedMissions, setClaimedMissions] = useState([]);

  // Daily Status now tracks specific progress for the day
  const [dailyStatus, setDailyStatus] = useState({
    date: new Date().toDateString(),
    completed: false,
    claimed: false,
    progress: 0
  });

  // --- STREAK & MISC TRACKING ---
  const [loginStreak, setLoginStreak] = useState(1);
  const [lastLoginDate, setLastLoginDate] = useState("");


  const [isMuted, setIsMuted] = useState(false);
  const toggleMute = () => {
    setIsMuted(!isMuted);
    checkDailyChallenge('INTERACTION', 'mute');
  };

  // --- DERIVED STATS ---
  const getActiveStats = () => {
    let multiplier = 1;
    let refundChance = 0;
    let discount = 0;
    let cpBoost = 0;
    let protection = 0;
    let luck = 0;

    Object.values(equipped).forEach(itemId => {
      const item = HARDWARE_ITEMS.find(i => i.id === itemId);
      if (item) {
        if (item.multiplier) multiplier *= item.multiplier;
        if (item.refundChance) refundChance += item.refundChance;
        if (item.discount) discount += item.discount;
        if (item.cpBoost) cpBoost += item.cpBoost;
        if (item.protection) protection += item.protection;
        if (item.luck) luck += item.luck;
      }
    });

    return {
      multiplier: parseFloat(multiplier.toFixed(2)),
      refundChance: parseFloat(refundChance.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      cpBoost: parseFloat(cpBoost.toFixed(2)),
      protection: parseFloat(protection.toFixed(2)),
      luck: parseFloat(luck.toFixed(2))
    };
  };
  const activeStats = getActiveStats();

  // --- GET ACTIVE CHALLENGE ---
  const getActiveChallenge = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    return DAILY_CHALLENGES[dayOfYear % DAILY_CHALLENGES.length];
  };
  const activeChallenge = getActiveChallenge();

  // --- UNIFIED CHECKER: DAILY CHALLENGES ---
  const checkDailyChallenge = useCallback((type, payload) => {
    if (dailyStatus.completed) return;

    const challenge = activeChallenge;
    if (!challenge) return;

    let isComplete = false;
    let newProgress = dailyStatus.progress;

    const currentHour = new Date().getHours();
    const isWeekend = new Date().getDay() === 0 || new Date().getDay() === 6;

    // Helper to increment progress for count-based challenges
    const incrementProgress = (target) => {
      newProgress += 1;
      if (newProgress >= target) isComplete = true;
    };

    // Helper to reset progress (for streaks)
    const resetProgress = () => {
      newProgress = 0;
    };

    switch (challenge.id) {
      // --- LOGIN & STREAKS ---
      case 'd1': if (loginStreak >= 3) isComplete = true; break;
      case 'd2': if (type === 'LOGIN' && currentHour < 12) isComplete = true; break;
      case 'd3': if (type === 'LOGIN' && currentHour >= 20) isComplete = true; break;
      case 'd4': if (type === 'LOGIN' && isWeekend) isComplete = true; break;
      case 'd5': if (type === 'LOGIN' && payload && payload.count >= 2) isComplete = true; break; // Needs session tracking, simplistic fallback

      // --- SPINS & BETTING ---
      case 'd6': if (type === 'SPIN' && payload.bet >= 500) isComplete = true; break;
      case 'd7':
        if (type === 'SPIN' && payload.bet <= 10) incrementProgress(10);
        break;
      case 'd8': if (type === 'SPIN' && payload.bet >= (balance / 2)) isComplete = true; break;
      case 'd9': if (type === 'SPIN') incrementProgress(20); break;
      case 'd10': if (type === 'SPIN') incrementProgress(50); break;
      case 'd11': if (type === 'WIN' && Math.abs(payload.amount - 777) < 10) isComplete = true; break; // Forgive slightly
      case 'd12':
        // Break Even Streak
        if (type === 'WIN') {
          if (payload.amount === payload.bet) incrementProgress(3);
          else resetProgress();
        }
        break;
      case 'd13':
        // Pity Streak (Losses)
        if (type === 'WIN') {
          if (payload.amount === 0) incrementProgress(5);
          else resetProgress();
        }
        break;
      case 'd14': if (type === 'WIN' && activeStats.cpBoost >= 1 && payload.amount > 0) isComplete = true; break; // Proxy for multiplier
      case 'd15': if (type === 'SPIN' && !payload.hasHeld) incrementProgress(5); break;

      // --- TRIVIA & KNOWLEDGE ---
      case 'd16': if (type === 'ANSWER_CORRECT') isComplete = true; break;
      case 'd17': if (type === 'ANSWER_CORRECT') incrementProgress(5); else if (type === 'ANSWER_WRONG') resetProgress(); break;
      case 'd18': if (type === 'ANSWER_WRONG') isComplete = true; break;
      case 'd19': if (type === 'LIFELINE' && payload === 'debug') isComplete = true; break;
      case 'd20': if (type === 'LIFELINE_WIN' && payload === 'fifty') isComplete = true; break;
      case 'd21': if (type === 'LIFELINE_WIN' && payload === 'hint') isComplete = true; break;
      case 'd22': if (type === 'ANSWER_SPEED' && payload <= 5) isComplete = true; break;
      case 'd23': if (type === 'INTERACTION' && payload === 'explanation') isComplete = true; break;
      case 'd24': if (type === 'ANSWER_CORRECT' && payload.category === 'SQL') incrementProgress(3); break;
      case 'd25':
        // Polyglot: Flip Python & SQL. Progress 0->1 (Python), 1->2 (SQL) or vice versa? 
        // Simpler: bitmask. 1=Python, 2=SQL. Target 3.
        if (type === 'FLIP') {
          if (payload.category === 'Python') newProgress |= 1;
          if (payload.category === 'SQL') newProgress |= 2;
          if (newProgress === 3) isComplete = true;
        }
        break;

      // --- CARDS & COLLECTION ---
      case 'd26': if (type === 'FLIP' && flippedCards.length + 1 >= 4) isComplete = true; break;
      case 'd27': if (type === 'FLIP') isComplete = true; break;
      case 'd28': if (type === 'FLIP' && flippedCards.length >= 10) isComplete = true; break; // Logicgap: flippedCards is resets daily? No, total?
      case 'd29': if (type === 'FLIP' && payload.category === 'Python') isComplete = true; break;
      case 'd30': if (type === 'FLIP' && payload.category === 'AI/ML') isComplete = true; break;
      case 'd31': if (type === 'FLIP' && payload.category === 'Logic') incrementProgress(4); break; // Assuming hard to track category streak.
      case 'd32': if (type === 'NAVIGATE' && payload === 'GEM_SHOP') isComplete = true; break;
      case 'd33': if (type === 'SPEND_GEMS') incrementProgress(50); break;
      case 'd34': if (type === 'INTERACTION' && payload.type === 'item_details') isComplete = true; break;
      case 'd35': if (type === 'EQUIP') isComplete = true; break;

      // --- EVENTS & BOSSES ---
      case 'd36': if (type === 'BOSS_TRIGGER') isComplete = true; break;
      case 'd37': if (type === 'BOSS_TURN') incrementProgress(3); break;
      case 'd38': if (type === 'BOSS_DEFEAT') isComplete = true; break;
      case 'd39': if (type === 'EVENT' && payload === 'GLITCH_FIX') isComplete = true; break;
      case 'd40': if (type === 'EVENT' && payload === 'INJECTION_BLOCK') isComplete = true; break;
      case 'd41': if (type === 'WIN' && payload.amount > 0 && balance < 100) isComplete = true; break;
      case 'd42': if (type === 'WIN' && balance === 0 && payload.amount > 0) isComplete = true; break;
      case 'd43': if (type === 'EVENT') incrementProgress(2); break;
      case 'd44': if (type === 'BOSS_DAMAGE' && payload >= 500) isComplete = true; break;
      case 'd45': if (type === 'SPIN' && !payload.isBoss) incrementProgress(50); else if (type === 'BOSS_TRIGGER') resetProgress(); break;

      // --- MISC ---
      case 'd46': if (type === 'INTERACTION') incrementProgress(5); break;
      case 'd47': if (type === 'INTERACTION' && payload === 'mute') isComplete = true; break;
      case 'd48': if (type === 'NAVIGATE') incrementProgress(4); break; // Needs unique pages but simplistic count fine
      case 'd49': if (activeStats.cpBoost && cp > 1000) isComplete = true; break; // Placeholder logic
      case 'd50': if (type === 'DAILY_COMPLETE') incrementProgress(3); break;

      default: break;
    }

    // Update state if progress changed or completed
    if (isComplete) {
      setDailyStatus(prev => ({ ...prev, completed: true, progress: newProgress }));
      setCompletedChallengeIds(prev => {
        if (!prev.includes(challenge.id)) {
          return [...prev, challenge.id];
        }
        return prev;
      });
    } else if (newProgress !== dailyStatus.progress) {
      setDailyStatus(prev => ({ ...prev, progress: newProgress }));
    }
  }, [dailyStatus.completed, dailyStatus.progress, activeChallenge, loginStreak, balance, activeStats.cpBoost, flippedCards.length, cp]);

  // --- UNIFIED CHECKER: PERMANENT MISSIONS ---
  const checkMissions = useCallback((type, payload) => {
    let newCompletions = [...completedMissions];
    let hasUpdates = false;

    MISSIONS.forEach(mission => {
      if (newCompletions.includes(mission.id)) return;
      const req = mission.requirement;
      if (!req) return;

      let isComplete = false;
      const userLevel = Math.floor(cp / 100) + 1;

      // --- LOGIC MAP ---
      switch (req.type) {
        case 'SPIN_TOTAL': if (totalSpins >= req.target) isComplete = true; break;
        case 'WIN_ANY': if (type === 'WIN' && payload > 0) isComplete = true; break;
        case 'ANSWER_TOTAL': if (questionsCorrect >= req.target) isComplete = true; break;
        case 'OPEN_SHOP': if (type === 'NAVIGATE' && payload === 'HARDWARE_SHOP') isComplete = true; break;
        case 'BUY_ITEM_ANY': if (type === 'BUY_HARDWARE') isComplete = true; break;
        case 'LEVEL_REACH': if (userLevel >= req.target) isComplete = true; break;
        case 'FLIP_CARD': if (type === 'FLIP') isComplete = true; break; // Assumes target is 1
        case 'ANSWER_STREAK': if (answerStreak >= req.target) isComplete = true; break;
        case 'BLOCK_INJECTION': if (type === 'EVENT' && payload === 'INJECTION_BLOCK') isComplete = true; break;
        case 'FIX_GLITCH': if (type === 'EVENT' && payload === 'GLITCH_FIX') isComplete = true; break;
        case 'WIN_SINGLE': if (type === 'WIN' && payload >= req.target) isComplete = true; break;
        case 'BALANCE_REACH': if (balance >= req.target) isComplete = true; break;
        case 'EQUIP_ALL_SLOTS':
          if (equipped['cpu'] && equipped['gpu'] && equipped['ram'] && equipped['psu']) isComplete = true;
          break;
        case 'BOSS_DEFEAT': if (type === 'BOSS_DEFEAT' && payload.level === req.target) isComplete = true; break;
        case 'DAILY_COMPLETE':
          // Assuming dailyStatus tracks completed count or we derive it differently. 
          // For now, this requires a state we might not have explicitly as a counter. 
          // Temporary bypass: check type 'DAILY_COMPLETE' with payload count
          if (type === 'DAILY_COMPLETE' && payload >= req.target) isComplete = true;
          break;
        case 'QUERY_CONTAINS':
          if (type === 'QUERY' && payload.includes(req.target)) isComplete = true;
          break;
        case 'QUERY_REGEX':
          if (type === 'QUERY' && req.target.test(payload)) isComplete = true;
          break;
        case 'EVENT_JACKPOT': if (type === 'JACKPOT') isComplete = true; break;
        case 'INVENTORY_COUNT': if (inventory.length >= req.target) isComplete = true; break;
        case 'EQUIP_TIER':
          const equippedId = equipped[req.category];
          if (equippedId) {
            const item = HARDWARE_ITEMS.find(i => i.id === equippedId);
            if (item && item.tier >= req.tier) isComplete = true;
          }
          break;
        case 'SPEND_TOTAL':
          // Needs totalSpent state. Using inventory length as proxy? No, bad. 
          // Logic: Skip for now or assume checked elsewhere. 
          break;
        case 'LIFELINE_USE_TOTAL': if (lifelinesUsed >= req.target) isComplete = true; break;
        case 'GEM_BALANCE': if (gems >= req.target) isComplete = true; break;
        case 'PRESTIGE_UNLOCK': if (prestigeLevel >= req.target) isComplete = true; break;
        case 'PRESTIGE_LEVEL': if (prestigeLevel >= 1 && userLevel >= req.target) isComplete = true; break;
        case 'MISSION_COUNT': if (newCompletions.length >= req.target) isComplete = true; break;
        case 'UNLOCK_COURSE': if (type === 'UNLOCK_COURSE' && payload >= req.target) isComplete = true; break;
        case 'INVENTORY_COMPLETE': if (inventory.length >= HARDWARE_ITEMS.length) isComplete = true; break;
        case 'BOSS_SURVIVE': if (type === 'BOSS_SURVIVE' && payload >= req.target) isComplete = true; break;
        default: break;
      }

      if (isComplete) {
        newCompletions.push(mission.id);
        hasUpdates = true;
      }
    });

    if (hasUpdates) setCompletedMissions(newCompletions);
  }, [completedMissions, cp, totalSpins, questionsCorrect, answerStreak, balance, equipped, inventory, lifelinesUsed, gems, prestigeLevel]);

  // --- INITIALIZATION: LOAD FROM BACKEND OR LOCALSTORAGE ---
  useEffect(() => {
    const loadData = async () => {
      if (isSignedIn && user) {
        // 1. Try fetching from Backend
        try {
          const data = await api.fetchSlotProgress(user.id);
          if (data && data.success && data.progress) {
            const p = data.progress;
            // Only update state if value exists in backend response
            if (p.balance !== undefined) setBalance(p.balance);
            if (p.cp !== undefined) setCp(p.cp);
            if (p.gems !== undefined) setGems(p.gems);
            if (p.totalSpins !== undefined) setTotalSpins(p.totalSpins);
            if (p.questionsAnswered !== undefined) setQuestionsAnswered(p.questionsAnswered);
            if (p.questionsCorrect !== undefined) setQuestionsCorrect(p.questionsCorrect);
            if (p.inventory) setInventory(p.inventory);
            if (p.equipped) setEquipped(p.equipped); // Works for both Map and Object due to earlier backend handling
            if (p.flippedCards) setFlippedCards(p.flippedCards);
            if (p.defeatedBosses) setDefeatedBosses(p.defeatedBosses);
            if (p.completedMissions) setCompletedMissions([...new Set(p.completedMissions.map(Number))]);
            if (p.claimedMissions) setClaimedMissions([...new Set(p.claimedMissions.map(Number))]);
            if (p.dailyStatus) setDailyStatus(p.dailyStatus);
            if (p.loginStreak !== undefined) setLoginStreak(p.loginStreak);
            if (p.lastLoginDate) setLastLoginDate(p.lastLoginDate);
            if (p.lastFlipDate) setLastFlipDate(p.lastFlipDate);
            if (p.answerStreak !== undefined) setAnswerStreak(p.answerStreak);
            if (p.lifelinesUsed !== undefined) setLifelinesUsed(p.lifelinesUsed);
            if (p.answerStreak !== undefined) setAnswerStreak(p.answerStreak);
            if (p.lifelinesUsed !== undefined) setLifelinesUsed(p.lifelinesUsed);
            if (p.prestigeLevel !== undefined) setPrestigeLevel(p.prestigeLevel);
            if (p.completedChallengeIds) setCompletedChallengeIds(p.completedChallengeIds);

            setDataLoaded(true);
            return;
          }
        } catch (error) {
          console.error("Failed to load backend data:", error);
        }
      }

      // 2. Default Initial State (Guest Mode - No Persistence)
      // We explicitly DO NOT load from localStorage to ensure clean state for testing/fun
      setDataLoaded(true);
    };

    if (isLoaded) {
      loadData();
    }
  }, [isLoaded, isSignedIn, user]);

  // --- SYNC TO BACKEND (DEBOUNCED) ---
  const syncTimeoutRef = useRef(null);

  useEffect(() => {
    // Don't sync until initial data load is done to prevent overwriting
    if (!dataLoaded) return;

    // 1. Sync to Backend (Debounced 2s)
    if (isSignedIn && user) {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

      syncTimeoutRef.current = setTimeout(() => {
        api.syncSlotProgress(user.id, {
          balance, cp, gems, totalSpins, questionsAnswered, questionsCorrect,
          inventory, equipped, flippedCards, defeatedBosses, completedMissions,
          claimedMissions, dailyStatus, loginStreak, lastLoginDate, lastFlipDate,
          // claimedMissions, dailyStatus, loginStreak, lastLoginDate, lastFlipDate,
          answerStreak, lifelinesUsed, prestigeLevel, completedChallengeIds
        });
      }, 2000);
    }
  }, [
    balance, cp, gems, totalSpins, questionsAnswered, questionsCorrect,
    inventory, equipped, flippedCards, defeatedBosses, completedMissions,
    claimedMissions, dailyStatus, loginStreak, lastLoginDate, lastFlipDate,
    isSignedIn, user, dataLoaded, answerStreak, lifelinesUsed, prestigeLevel, completedChallengeIds
  ]);


  // --- EFFECT: DAILY LOGIC ---
  useEffect(() => {
    if (!dataLoaded) return;

    const today = new Date().toDateString();

    // 1. Daily Challenge Reset
    if (dailyStatus.date !== today) {
      setDailyStatus({ date: today, completed: false, claimed: false, progress: 0 });

    }

    // 2. Flip Card Reset
    if (lastFlipDate !== today) {
      setFlippedCards([]);
      setLastFlipDate(today);
    }

    // 3. Login Streak Logic
    if (lastLoginDate !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      if (lastLoginDate === yesterday.toDateString()) {
        setLoginStreak(prev => prev + 1);
      } else {
        setLoginStreak(1);
      }
      setLastLoginDate(today);

      // Trigger Login Check immediately
      checkDailyChallenge('LOGIN', null);
    }
  }, [dailyStatus.date, lastFlipDate, lastLoginDate, dataLoaded, checkDailyChallenge]);



  // --- ACTIONS ---
  const addCp = (amount) => {
    const boost = 1 + (activeStats.cpBoost || 0);
    setCp((prev) => prev + Math.floor(amount * boost));
  };

  const incrementSpins = (bet = 0) => {
    setTotalSpins(prev => prev + 1);
    checkDailyChallenge('SPIN', { bet });
  };

  const incrementQuestionsAnswered = (isCorrect) => {
    setQuestionsAnswered(prev => prev + 1);

    if (isCorrect) {
      setQuestionsCorrect(prev => prev + 1);
      setAnswerStreak(prev => {
        const newStreak = prev + 1;
        checkMissions('ANSWER_STREAK', newStreak); // Re-trigger check
        return newStreak;
      });
      checkDailyChallenge('ANSWER_CORRECT', null);
    } else {
      setAnswerStreak(0);
    }
    checkDailyChallenge('ANSWER', { correct: isCorrect });
  };

  const trackLifelineUse = (lifelineType) => {
    setLifelinesUsed(prev => prev + 1);
    checkDailyChallenge('LIFELINE', lifelineType);
    checkMissions('LIFELINE_USE_TOTAL', lifelinesUsed + 1);
  };

  const trackInteraction = (elementId) => {
    checkDailyChallenge('INTERACTION', elementId);
  };

  const markBossDefeated = (level, name) => {
    if (!defeatedBosses.includes(level)) {
      setDefeatedBosses(prev => [...prev, level]);
      checkMissions('BOSS_DEFEAT', { level, name });
      checkDailyChallenge('BOSS_DEFEAT', null);
    }
  };

  const parseAndGrantReward = (rewardStr) => {
    const val = parseInt(rewardStr.replace(/,/g, '').match(/\d+/)) || 0;

    if (rewardStr.includes("CP") || rewardStr.includes("XP")) addCp(val); // Handle legacy XP string too just in case
    else if (rewardStr.includes("Gem")) setGems(prev => prev + val);
    else if (rewardStr.includes("Coin") || rewardStr.includes("$") || rewardStr.includes("Chips")) setBalance(prev => prev + val);
    else if (rewardStr.includes("CPU")) setInventory(prev => [...prev, 'cpu_1']);
  };

  const claimMissionReward = (missionId) => {
    if (claimedMissions.includes(missionId)) return false;
    const mission = MISSIONS.find(m => m.id === missionId);
    if (!mission) return false;
    parseAndGrantReward(mission.reward);
    setClaimedMissions(prev => [...prev, missionId]);
    return true;
  };

  const claimDailyReward = () => {
    if (dailyStatus.claimed || !dailyStatus.completed) return false;
    if (!activeChallenge) return false;
    parseAndGrantReward(activeChallenge.reward);
    setDailyStatus(prev => ({ ...prev, claimed: true }));
    return true;
  };

  const purchaseItem = (itemId, cost) => {
    if (balance >= cost && !inventory.includes(itemId)) {
      setBalance(prev => prev - cost);
      setInventory(prev => [...prev, itemId]);

      const item = HARDWARE_ITEMS.find(i => i.id === itemId);
      if (item) {
        setEquipped(prev => ({ ...prev, [item.type]: itemId }));
        checkDailyChallenge('EQUIP', null);
      }
      return true;
    }
    return false;
  };

  const handleFlipCard = (cardId, category) => {
    if (!flippedCards.includes(cardId)) {
      const newFlips = [...flippedCards, cardId];
      setFlippedCards(newFlips);
      checkDailyChallenge('FLIP', { category });
    }
  };

  const resetGame = () => {
    if (window.confirm("FACTORY RESET: Are you sure? All data including missions will be wiped.")) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const value = {
    balance, setBalance,
    cp, addCp,
    gems, setGems,
    totalSpins, incrementSpins,
    questionsAnswered, incrementQuestionsAnswered,
    checkMissions, checkDailyChallenge, trackInteraction, trackLifelineUse,
    inventory, setInventory,
    equipped, setEquipped,
    purchaseItem,
    flippedCards, setFlippedCards: handleFlipCard,
    defeatedBosses, markBossDefeated,
    completedMissions, claimedMissions, claimMissionReward,
    dailyStatus, claimDailyReward, completedChallengeIds,
    activeStats,
    activeChallenge,
    user: { level: Math.floor(cp / 100) + 1 },
    resetGame, isMuted, toggleMute
  };

  return <GameContext.Provider value={value}>{!isLoaded ? <Loader /> : children}</GameContext.Provider>;
};

export default GameContext;